/* eslint react-hooks/rules-of-hooks: 'warn' */

import { FC, useEffect, useState } from 'react';
import { Checkbox, Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import styled from '@emotion/styled';

import { Log, useUBL } from '@common/log-manager';

import { ShopUxUbl, TextAndSortingComponentType } from '../../types';
import { FilterParam } from '../../types/param';
import { SortButton } from './sort-button';

interface ComponentProps<T> {
  component: T;
}

interface Props extends ComponentProps<Omit<TextAndSortingComponentType, 'type'>> {
  active_navigation_id?: string | null;
  onChangeFilter: (params: FilterParam) => void;
  log?: Partial<Log> & { navigation: string };
  is_srp?: boolean;
}

export const TextAndSortingFilter: FC<React.PropsWithChildren<Props>> = ({
  component: sort_component,
  active_navigation_id,
  onChangeFilter,
  log,
  is_srp,
}) => {
  const tracker = useUBL();
  const {
    main_title,
    sub_title,
    check_button_item_list = [],
    sorting_item_list = [],
    zigzin_filter_item_list = [],
  } = sort_component;

  const [check_button_ids, setCheckButtonId] = useState<string[]>(() =>
    check_button_item_list
      .filter((check_button_item) => check_button_item.selected)
      .map((check_button_item) => check_button_item.id),
  );

  const [sub_filter_id_list, setSubFilterIdList] = useState<string[]>(() =>
    zigzin_filter_item_list
      .filter((check_button_item) => check_button_item.selected)
      .map((check_button_item) => check_button_item.id),
  );
  const [sorting_list, setSortingList] = useState(sorting_item_list);

  useEffect(() => initFilter(), [active_navigation_id]);

  const initFilter = () => {
    setCheckButtonId([]);
    setSubFilterIdList([]);
  };

  const changeCheckBoxItem = (id: string, checked: boolean, ubl?: ShopUxUbl | null) => {
    if (log) {
      const data = is_srp
        ? {
            ...(ubl?.server_log ?? {}),
            ...(is_srp ? { is_toggled: checked } : {}),
          }
        : (ubl?.server_log ?? null);
      tracker({
        ...log,
        category: 'click',
        object_type: 'filter',
        object_id: is_srp ? (id.toString() === '100' ? 'free_shipping' : 'zigzin') : String(id),
        data,
      });
    }
    setCheckButtonId((prev) => {
      const new_check_button_ids = checked ? [...prev, id] : prev.filter((p) => p !== id);
      const sorting_item_id = sorting_list.filter((item) => item.selected)[0]?.id || '';
      onChangeFilter({
        check_button_item_ids: new_check_button_ids,
        sorting_item_id,
        sub_filter_id_list: [],
      });
      setSubFilterIdList([]);
      return new_check_button_ids;
    });
  };

  const changeSortItem = (id: string, ubl?: ShopUxUbl | null) => {
    if (log) {
      tracker({
        ...log,
        category: 'click',
        object_type: 'filter',
        object_id: ubl?.object?.id ? String(ubl.object.id) : String(id),
        data: ubl?.server_log ?? null,
      });
    }
    setSortingList((prev) => {
      onChangeFilter({
        check_button_item_ids: check_button_ids,
        sorting_item_id: id,
        sub_filter_id_list,
      });

      return prev.map((data) => ({
        ...data,
        selected: data.id === id,
      }));
    });
  };

  const changeCheckZigzinFilter = (id: string, checked: boolean) => {
    setSubFilterIdList((prev) => {
      const new_sub_filter_ids = checked ? [...prev, id] : prev.filter((p) => p !== id);
      const sorting_item_id = sorting_list.filter((item) => item.selected)[0]?.id || '';
      onChangeFilter({
        check_button_item_ids: check_button_ids,
        sorting_item_id,
        sub_filter_id_list: new_sub_filter_ids,
      });
      return new_sub_filter_ids;
    });
  };

  if (!sort_component) {
    return null;
  }

  return (
    <>
      <Wrap className='filter'>
        {main_title?.text && (
          <MainTitle as='p' color='textStrong' variant='Body_17_SemiBold'>
            {main_title.text}
          </MainTitle>
        )}
        <FilterWrap>
          {sub_title?.text && (
            <Text as='span' color='textSecondary' variant='Caption_11_Bold'>
              {sub_title.text}
            </Text>
          )}

          {check_button_item_list.map(({ id, name, disabled, ubl }) => (
            <Checkbox
              key={id}
              size='small'
              style={{ height: '100%' }}
              disabled={disabled || false}
              checked={check_button_ids.includes(id)}
              onChange={(checked) => changeCheckBoxItem(id, checked, ubl)}
            >
              <span>{name ?? ''}</span>
            </Checkbox>
          ))}

          <SortButtonWrap>
            {sorting_list.length > 0 && <SortButton sort_item_list={sorting_list} onChange={changeSortItem} />}
          </SortButtonWrap>
        </FilterWrap>
      </Wrap>

      {zigzin_filter_item_list.length > 0 && (
        <SubFilterContainer>
          {zigzin_filter_item_list.map(({ id, name, html_name, disabled }) => (
            <Checkbox
              key={id}
              size='small'
              style={{ height: '100%' }}
              disabled={disabled || false}
              checked={sub_filter_id_list.includes(id)}
              onChange={(checked) => changeCheckZigzinFilter(id, checked)}
            >
              <span dangerouslySetInnerHTML={{ __html: html_name?.normal ?? name ?? '' }} />
            </Checkbox>
          ))}
        </SubFilterContainer>
      )}
    </>
  );
};

const Wrap = styled.section`
  padding: 0 16px;
  position: relative;
`;

const MainTitle = styled(Text)`
  padding: 14px 0 0 0;
  height: 41px;
  letter-spacing: -0.3px;
`;

const FilterWrap = styled.div`
  display: flex;
  align-items: center;
  height: 48px;

  label + label {
    margin-left: 12px;
  }
`;

const SortButtonWrap = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const SubFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 48px;
  padding: 0 16px;
  border-top: 1px solid ${vars.color.dividerThin};
`;
